/**
 * Payment API client
 *
 * Calls the Express backend for PayPlus payment operations.
 */

import { apiGet, apiPost } from "./apiClient";

export interface CreatePaymentParams {
  type: "subscription" | "token_pack";
  planId?: string;
  billingPeriod?: "monthly" | "annual";
  tokens?: number;
  couponCode?: string;
}

interface CreatePaymentResponse {
  paymentUrl: string;
  pageRequestUid: string;
}

interface VerifyPaymentResponse {
  status: "pending" | "completed" | "failed";
  type?: string;
  planId?: string;
  credits?: number;
  errorMessage?: string;
  /** Referral bonus the referred friend just earned by subscribing — drives
   *  the referee celebration toast on the payment-result screen. */
  referralBonusCredits?: number;
}

export async function createPayment(
  params: CreatePaymentParams,
): Promise<CreatePaymentResponse> {
  return apiPost<CreatePaymentResponse>("/api/payments/create-payment", params);
}

export async function verifyPayment(
  pageRequestUid: string,
): Promise<VerifyPaymentResponse> {
  return apiPost<VerifyPaymentResponse>("/api/payments/verify", {
    pageRequestUid,
  });
}

// ── Cancellation / Retention ────────────────────

import type { CancellationReason } from "@shared/schemas/payments";

interface CancelSubscriptionParams {
  reason: CancellationReason;
  feedback?: string;
}

export async function cancelSubscription(
  params: CancelSubscriptionParams,
): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>(
    "/api/payments/cancel-subscription",
    params,
  );
}

interface ApplyDiscountParams {
  reason: CancellationReason;
  feedback?: string;
}

export async function applyDiscount(
  params: ApplyDiscountParams,
): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>("/api/payments/apply-discount", params);
}

export interface SubscriptionInfo {
  status: string;
  planId: string;
  billingPeriod: string;
  currentPeriodEnd: string | null;
  creditsRemaining: number;
  imagesGenerated: number;
  videosGenerated: number;
}

export async function getSubscriptionInfo(): Promise<SubscriptionInfo> {
  return apiGet<SubscriptionInfo>("/api/payments/subscription-info");
}

export async function downgradeToFree(): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>("/api/payments/downgrade-to-free", {});
}

export async function reactivateSubscription(params?: {
  planId?: string;
  billingPeriod?: "monthly" | "annual";
}): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>("/api/payments/reactivate-subscription", params ?? {});
}

// ── Scheduled Downgrade ────────────────────

export async function scheduleDowngrade(params: {
  planId: string;
  billingPeriod: "monthly" | "annual";
}): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>("/api/payments/schedule-downgrade", params);
}

export async function cancelScheduledDowngrade(): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>("/api/payments/cancel-scheduled-downgrade", {});
}

// ── Coupon Validation ────────────────────

export interface ValidateCouponResponse {
  valid: boolean;
  discountPercent?: number;
  discountAmount?: number;
  currencyCode?: string;
  message?: string;
}

export async function validateCoupon(
  code: string,
): Promise<ValidateCouponResponse> {
  return apiPost<ValidateCouponResponse>("/api/payments/validate-coupon", {
    code,
  });
}

export interface SubscriptionEvent {
  id: string;
  event_type: string;
  plan_id: string | null;
  billing_period: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export async function fetchSubscriptionEvents(
  limit = 50,
): Promise<SubscriptionEvent[]> {
  const res = await apiGet<{ events: SubscriptionEvent[] }>(
    `/api/payments/subscription-events?limit=${limit}`,
  );
  return res.events;
}
